import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from './api';

interface Result<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Fetches a path and re-fetches when it changes. Requests are aborted on
 * unmount, and a stale response from a superseded path is discarded rather
 * than allowed to overwrite fresher data.
 *
 * Pass `path: null` to skip the request entirely — used where a screen must
 * wait for a period or an id to resolve first.
 */
export function useApi<T>(path: string | null, deps: unknown[] = []): Result<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const [nonce, setNonce] = useState(0);
  const latest = useRef(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (path === null) {
      setData(null);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const ticket = ++latest.current;
    setLoading(true);
    setError(null);

    api
      .get<T>(path, controller.signal)
      .then((res) => {
        if (ticket === latest.current) {
          setData(res);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (ticket !== latest.current) return;
        setError(
          err instanceof ApiError
            ? err
            : new ApiError(0, (err as Error)?.message ?? 'Request failed', null),
        );
        setLoading(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce, ...deps]);

  return { data, error, loading, reload };
}
